import MinIODataSource from '@rendora/common/features/upload/minio-upload/data-source';
import XMinIOUpload from '@rendora/common/features/upload/minio-upload/service';
import DataSource from '@rendora/common/features/upload/oss-upload/data-source';
import XOssService from '@rendora/common/features/upload/oss-upload/service';
import type { IBizParams } from '@rendora/common/features/upload/oss-upload/types';
import { useUserStore } from '@rendora/common/store/user';
import {
  COLLECTION_MAP,
  MEDIA_TYPE,
  RESOURCE_TYPE,
  UPLOAD_STATUS,
} from '@rendora/common/types/enums';
import { isPrivate } from '@rendora/common/utils/config';
import { ref } from 'vue';

export enum ACCEPT {
  IMAGE = '.png,.jpg,.jpeg,.gif',
  VIDEO = '.mp4,.mov,.webm,.mkv',
  AUDIO = '.mp3,.wav',
  PPT = '.ppt,.pptx',
  PDF = '.pdf',
  FONT = '.ttf,.woff,.otf',
}

export const MB_ATOM = Math.pow(1024, 2);

export const FILE_SIZE = {
  IMAGE: 'image_limit',
  VIDEO: 'video_limit',
  AUDIO: 'audio_limit',
  PPT: 'ppt_limit',
  PDF: 'pdf_limit',
  FONT: 'font_limit',
};

interface DataType {
  model_type?: COLLECTION_MAP;
  resource_type?: RESOURCE_TYPE;
  media_type?: MEDIA_TYPE;
  source?: unknown;
  upload_type?: unknown;
  [key: string]: unknown;
}

export class IFile {
  uid: string;
  percent?: number;
  upload_status: number;
  name: string;
  size: number;
  file?: File;
  constructor(file: File) {
    this.uid = `uid-${+Date.now()}`;
    this.percent = 0;
    this.upload_status = UPLOAD_STATUS.NO_START;
    this.name = file.name;
    this.size = file.size;
    this.file = file;
  }
}

interface UploadOptions {
  accept?: string;
  params?: DataType;
  checkSize?: boolean;
  beforeUpload?: (file: IFile, fileList?: FileList) => boolean;
  onError?: (file: IFile, errorMessage?: string) => void;
  onChange?: (file: IFile) => void;
  onSuccess?: (file: IFile, data: Parameters<XOssService['onSuccess']>[0]) => void;
  onProgress?: (file: IFile) => void;
}

// 根据环境自动选择 OSS 或 MinIO 上传
export const useUpload = (options: UploadOptions) => {
  const userInfo = useUserStore().user;
  if (!userInfo) return;

  const isLoading = ref(false);

  const simpleUpload = async (file: IFile): Promise<void> => {
    // 触发onChange事件
    options.onChange?.(file);

    // 根据 isPrivate() 选择使用 OSS 还是 MinIO
    const isPrivateMode = isPrivate();

    if (isPrivateMode) {
      // 私有化环境使用 MinIO
      const minioService = new XMinIOUpload({
        auth: {
          user_id: userInfo?.user_id,
        },
        dataSource: MinIODataSource,
        params: options.params as IBizParams,
        onProgress: uploadProgress => {
          file.percent = uploadProgress;
          file.upload_status = UPLOAD_STATUS.UPLOADING;
          options?.onProgress?.(file);
        },
        onFail: errorMessage => {
          isLoading.value = false;
          file.percent = 100;
          file.upload_status = UPLOAD_STATUS.ERROR;
          options.onError?.(file, errorMessage);
        },
        onSuccess: responseData => {
          isLoading.value = false;
          file.percent = 100;
          file.upload_status = UPLOAD_STATUS.FINISHED;
          options.onSuccess?.(file, responseData);
        },
      });

      isLoading.value = true;
      if (file.file) {
        await minioService.simpleUpload(file.file as File & { uid: string });
      }
    } else {
      // 非私有化环境使用 OSS
      const ossService = new XOssService({
        auth: {
          user_id: userInfo?.user_id,
        },
        dataSource: DataSource,
        params: options.params as IBizParams,
        onProgress: uploadProgress => {
          file.percent = uploadProgress;
          file.upload_status = UPLOAD_STATUS.UPLOADING;
          options?.onProgress?.(file);
        },
        onFail: errorMessage => {
          isLoading.value = false;
          file.percent = 100;
          file.upload_status = UPLOAD_STATUS.ERROR;
          options.onError?.(file, errorMessage);
        },
        onSuccess: responseData => {
          isLoading.value = false;
          file.percent = 100;
          file.upload_status = UPLOAD_STATUS.FINISHED;
          options.onSuccess?.(file, responseData);
        },
      });

      isLoading.value = true;
      if (file.file) {
        await ossService.simpleUpload(file.file as File & { uid: string });
      }
    }
  };

  return {
    loading: isLoading,
    simpleUpload,
  };
};
